import { type MaybeRefOrGetter, toRaw, toValue } from 'vue'

import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { get } from '@backmarket/utils/object/get'
import { createSharedComposable } from '@vueuse/core'

import type {
  GooglePayIsReadyToPayRequest,
  GooglePayPaymentDataRequest,
  GooglePayPaymentsClient,
  GooglePayTransactionInfo,
} from '../../../../../types/google-pay'
import { isValidGooglePayPaymentMethodConfig } from '../../../helpers/isValidConfig'
import { PaymentError } from '../../../types/PaymentError'
import { PaymentMethodMisconfiguredError } from '../../../types/PaymentMethodMisconfiguredError'

import { useGooglePayLibrary } from './useGooglePayLibrary'

const BASE_REQUEST = {
  apiVersion: 2,
  apiVersionMinor: 0,
}

const useGooglePayClient = (method: MaybeRefOrGetter<PaymentMethod | null>) => {
  const { getGooglePayLibrary } = useGooglePayLibrary()

  function rawConfig() {
    const raw = toRaw(toValue(method))?.config

    if (isValidGooglePayPaymentMethodConfig(raw)) {
      return raw
    }

    throw new PaymentMethodMisconfiguredError(
      'Invalid Google Pay configuration',
    )
  }

  let paymentsClientPromise: Promise<GooglePayPaymentsClient> | undefined
  async function getPaymentsClient() {
    if (!paymentsClientPromise) {
      const { environment } = rawConfig()
      paymentsClientPromise = getGooglePayLibrary()
        .then(
          (library) => new library.payments.api.PaymentsClient({ environment }),
        )
        .catch((error) => {
          paymentsClientPromise = undefined
          throw error
        })
    }

    return paymentsClientPromise
  }

  function getIsReadyToPayRequest(): GooglePayIsReadyToPayRequest {
    const { type, parameters } = rawConfig()

    return {
      ...BASE_REQUEST,
      allowedPaymentMethods: [{ type, parameters }],
      existingPaymentMethodRequired: true,
    }
  }

  function getPaymentDataRequest(
    transactionInfo: GooglePayTransactionInfo,
  ): GooglePayPaymentDataRequest {
    const { type, parameters, tokenizationSpecification, merchantInfo } =
      rawConfig()

    return {
      ...BASE_REQUEST,
      transactionInfo,
      merchantInfo,
      allowedPaymentMethods: [{ type, parameters, tokenizationSpecification }],
    }
  }

  /**
   * Checks if the user can pay with Google Pay
   *
   * Return true if the user has a payment method available, false otherwise
   *
   * Throws a PaymentError if the user can't pay with Google Pay
   */
  let readinessPromise: Promise<boolean> | undefined
  function isReadyToPay(): Promise<boolean> {
    if (!readinessPromise) {
      readinessPromise = getPaymentsClient()
        .then(async (client) => {
          const request = getIsReadyToPayRequest()
          const { result, paymentMethodPresent } =
            await client.isReadyToPay(request)

          if (!result) {
            throw new PaymentError('Google Pay is not ready to pay', {
              type: '/errors/payment/google-pay-not-ready',
            })
          }

          return paymentMethodPresent === true
        })
        .catch((error) => {
          readinessPromise = undefined
          throw error
        })
    }

    return readinessPromise
  }

  /**
   * Collects browser info for 3DS
   *
   * Required by Aduen
   * @see https://docs.adyen.com/api-explorer/Checkout/latest/post/payments#request-browserInfo
   *
   * This code is a temporary solution until we implement the GooglePay Adyen component copied from :
   * @see https://github.com/Adyen/adyen-web/blob/0ebf396b6416cac0b195fa1516d6a023d3f92728/packages/lib/src/utils/browserInfo.ts
   *
   * TODO: Implement GooglePay Adyen component instead of using the GooglePay API
   * @see https://docs.adyen.com/payment-methods/google-pay/web-component
   */
  function collectBrowserInfo() {
    const colorDepth = get(window, 'screen.colorDepth') || ''
    const javaEnabled = get(window, 'navigator.javaEnabled')
      ? window.navigator.javaEnabled()
      : false
    const screenHeight = get(window, 'screen.height') || ''
    const screenWidth = get(window, 'screen.width') || ''
    const userAgent = get(window, 'navigator.userAgent') || ''

    const language = get(window, 'navigator.language') || 'en'
    const d = new Date()
    const timeZoneOffset = d.getTimezoneOffset()

    return {
      acceptHeader: '*/*',
      colorDepth,
      language,
      javaEnabled,
      screenHeight,
      screenWidth,
      userAgent,
      timeZoneOffset,
    }
  }

  return {
    isReadyToPay,
    getPaymentsClient,
    getIsReadyToPayRequest,
    getPaymentDataRequest,
    collectBrowserInfo,
  }
}

/**
 * Share the Google Pay client between callers to avoid having multiple instance of Google Pay client doing the same work twice.
 * /!\ Because of this, the config used to create the client is the one of the first caller.
 */
export const useGooglePay = createSharedComposable(useGooglePayClient)

export type UseGooglePay = ReturnType<typeof useGooglePay>
